import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Avatar,
} from '@mui/material';
import {
  School,
  Assessment,
  Security,
  Speed,
  TrendingUp,
  EmojiEvents,
  Groups,
  AutoGraph,
  ArrowForward,
} from '@mui/icons-material';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <School sx={{ fontSize: 50 }} />,
      title: 'Easy Result Management',
      description: 'View and manage exam results efficiently with our intuitive platform.',
      color: '#667eea',
    },
    {
      icon: <Assessment sx={{ fontSize: 50 }} />,
      title: 'Comprehensive Reports',
      description: 'Generate detailed performance reports and analytics for students.',
      color: '#f093fb',
    },
    {
      icon: <Security sx={{ fontSize: 50 }} />,
      title: 'Secure & Private',
      description: 'Your data is protected with industry-standard security measures.',
      color: '#4facfe',
    },
    {
      icon: <Speed sx={{ fontSize: 50 }} />,
      title: 'Fast & Reliable',
      description: 'Access your results anytime, anywhere with our reliable system.',
      color: '#43e97b',
    },
  ];

  const studentImage = `${process.env.PUBLIC_URL || ''}/images/student.jpg`;

  return (
    <Box sx={{ 
      overflow: 'hidden',
      background: 'linear-gradient(180deg, #f8f9ff 0%, #ffffff 100%)',
    }}>
      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            animation: 'slideBackground 20s linear infinite',
          },
          '@keyframes slideBackground': {
            '0%': { backgroundPosition: '0 0' },
            '100%': { backgroundPosition: '60px 60px' },
          },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            position: 'relative',
            py: { xs: 8, sm: 10, md: 12 },
            px: { xs: 2, sm: 3 },
            minHeight: { xs: '100vh', md: 'auto' },
            overflow: 'hidden',
            backgroundColor: 'transparent',
          }}
        >
          {/* BACKGROUND IMAGE */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              height: '120%',
              minHeight: '100vh',
              backgroundImage: `
                linear-gradient(
                  135deg, 
                  rgba(102, 126, 234, 0.75) 0%, 
                  rgba(118, 75, 162, 0.65) 100%
                ), 
                url(${studentImage})
              `,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              opacity: 0.9,
              filter: 'blur(2px) brightness(0.85)',
              transform: 'scale(1.1)',
              willChange: 'transform',
              zIndex: 0,
              animation: 'zoomPulse 15s ease-in-out infinite',
              '@keyframes zoomPulse': {
                '0%, 100%': { transform: 'scale(1.1)' },
                '50%': { transform: 'scale(1.15)' },
              },
            }}
          />

          {/* CONTENT */}
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            {/* LEFT LOGO - Absolute Position */}
            <Box sx={{ 
              position: 'absolute',
              left: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 3,
              zIndex: 3,
              animation: 'fadeInLeft 1s ease-out',
              '@keyframes fadeInLeft': {
                '0%': { opacity: 0, transform: { xs: 'translate(-50%, -30px)', md: 'translate(0, -50%) translateX(-30px)' } },
                '100%': { opacity: 1, transform: { xs: 'translate(-50%, 0)', md: 'translateY(-50%)' } },
              },
            }}>
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    margin: 'auto',
                    width: 280,
                    height: 280,
                    background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(40px)',
                    animation: 'pulse 3s ease-in-out infinite',
                  }}
                />
                <Box
                  component="img"
                  src="/images/supunlogo.jpeg"
                  alt="Supun Rajapaksha"
                  sx={{
                    width: { xs: 150, md: 220 },
                    height: { xs: 150, md: 220 },
                    borderRadius: 4,
                    position: 'relative',
                    filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.4))',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05) rotate(2deg)',
                    },
                  }}
                />
              </Box>
              <Typography sx={{ 
                color: 'white', 
                fontWeight: 900, 
                fontSize: { xs: '1.2rem', md: '1.8rem' },
                textShadow: '2px 4px 12px rgba(0,0,0,0.3)',
                letterSpacing: '2px',
                textAlign: 'center',
              }}>
                Supun Rajapaksha
              </Typography>
            </Box>

            {/* RIGHT LOGO - Absolute Position */}
            <Box sx={{ 
              position: 'absolute',
              right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 3,
              zIndex: 3,
              animation: 'fadeInRight 1s ease-out',
              '@keyframes fadeInRight': {
                '0%': { opacity: 0, transform: { xs: 'translate(50%, 30px)', md: 'translate(0, -50%) translateX(30px)' } },
                '100%': { opacity: 1, transform: { xs: 'translate(50%, 0)', md: 'translateY(-50%)' } },
              },
            }}>
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    margin: 'auto',
                    width: 280,
                    height: 280,
                    background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(40px)',
                    animation: 'pulse 3s ease-in-out infinite 0.5s',
                  }}
                />
                <Box
                  component="img"
                  src="/images/wasithalogo.png"
                  alt="Wasitha Dissanayake"
                  sx={{
                    width: { xs: 150, md: 220 },
                    height: { xs: 150, md: 220 },
                    borderRadius: 4,
                    position: 'relative',
                    filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.4))',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05) rotate(-2deg)',
                    },
                  }}
                />
              </Box>
              <Typography sx={{ 
                color: 'white', 
                fontWeight: 900, 
                fontSize: { xs: '1.2rem', md: '1.8rem' },
                textShadow: '2px 4px 12px rgba(0,0,0,0.3)',
                letterSpacing: '2px',
                textAlign: 'center',
              }}>
                Wasitha Dissanayake
              </Typography>
            </Box>

            {/* CENTER */}
            <Grid container justifyContent="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ 
                  textAlign: 'center', 
                  maxWidth: 900, 
                  mx: 'auto',
                  px: { xs: 2, sm: 3 },
                  animation: 'fadeInUp 1s ease-out 0.3s backwards',
                  '@keyframes fadeInUp': {
                    '0%': { opacity: 0, transform: 'translateY(30px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                }}>
                  <Chip
                    label="🌟 #1 Education Platform"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.25)',
                      backdropFilter: 'blur(10px)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                      py: { xs: 2.5, md: 3 },
                      px: { xs: 1.5, md: 2 },
                      mb: { xs: 3, md: 0 },
                      border: '1px solid rgba(255,255,255,0.3)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    }}
                  />

                  {/* MOBILE LOGOS - Horizontal Layout */}
                  <Box sx={{ 
                    display: { xs: 'flex', md: 'none' },
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 4,
                    my: 4,
                    flexWrap: 'wrap',
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      gap: 1,
                    }}>
                      <Box
                        component="img"
                        src="/images/supunlogo.jpeg"
                        alt="Supun Rajapaksha"
                        sx={{
                          width: 90,
                          height: 90,
                          borderRadius: 3,
                          border: '3px solid rgba(255,255,255,0.3)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                          transition: 'transform 0.3s ease',
                          '&:active': {
                            transform: 'scale(0.95)',
                          },
                        }}
                      />
                      <Typography sx={{ 
                        color: 'white', 
                        fontWeight: 700, 
                        fontSize: '0.75rem',
                        textShadow: '1px 2px 6px rgba(0,0,0,0.3)',
                        textAlign: 'center',
                        letterSpacing: '0.5px',
                      }}>
                        Supun Rajapaksha
                      </Typography>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      gap: 1,
                    }}>
                      <Box
                        component="img"
                        src="/images/wasithalogo.png"
                        alt="Wasitha Dissanayake"
                        sx={{
                          width: 90,
                          height: 90,
                          borderRadius: 3,
                          border: '3px solid rgba(255,255,255,0.3)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                          transition: 'transform 0.3s ease',
                          '&:active': {
                            transform: 'scale(0.95)',
                          },
                        }}
                      />
                      <Typography sx={{ 
                        color: 'white', 
                        fontWeight: 700, 
                        fontSize: '0.75rem',
                        textShadow: '1px 2px 6px rgba(0,0,0,0.3)',
                        textAlign: 'center',
                        letterSpacing: '0.5px',
                      }}>
                        Wasitha Dissanayake
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography
                    sx={{
                      fontSize: { xs: '2.5rem', sm: '3.5rem', md: '5.5rem' },
                      fontWeight: 900,
                      color: 'white',
                      textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                      lineHeight: 1.1,
                      mb: { xs: 2, md: 3 },
                    }}
                  >
                    Welcome to Videsa 🎓
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: '1.25rem', sm: '1.6rem', md: '2.2rem' },
                      fontWeight: 700,
                      color: 'rgba(255,255,255,0.95)',
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      mb: { xs: 3, md: 4 },
                      letterSpacing: '0.5px',
                    }}
                  >
                    Exam Results Management System
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.15rem', md: '1.3rem' },
                      lineHeight: { xs: 1.6, md: 1.8 },
                      color: 'rgba(255,255,255,0.9)',
                      textShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      maxWidth: 650,
                      mx: 'auto',
                      mb: { xs: 4, md: 6 },
                    }}
                  >
                    A powerful digital platform for managing exam results, analyzing student
                    performance, and driving academic excellence across institutions.
                  </Typography>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 2, sm: 3 },
                    justifyContent: 'center', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    px: { xs: 2, sm: 0 },
                  }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => !isAuthenticated && navigate('/register')}
                      sx={{
                        bgcolor: 'white',
                        color: '#667eea',
                        px: { xs: 4, sm: 5, md: 6 },
                        py: { xs: 2, md: 2.5 },
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                        fontWeight: 900,
                        borderRadius: '50px',
                        textTransform: 'none',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                        transition: 'all 0.3s ease',
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: '280px', sm: 'auto' },
                        '&:hover': {
                          bgcolor: '#f8f9ff',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 15px 50px rgba(0,0,0,0.4)',
                        },
                      }}
                    >
                      Get Started Free →
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => !isAuthenticated && navigate('/login')}
                      sx={{
                        borderColor: 'white',
                        borderWidth: 2,
                        color: 'white',
                        px: { xs: 4, sm: 5, md: 6 },
                        py: { xs: 2, md: 2.5 },
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                        fontWeight: 900,
                        borderRadius: '50px',
                        textTransform: 'none',
                        backdropFilter: 'blur(10px)',
                        bgcolor: 'rgba(255,255,255,0.1)',
                        transition: 'all 0.3s ease',
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: '280px', sm: 'auto' },
                        '&:hover': {
                          borderColor: 'white',
                          borderWidth: 2,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          transform: 'translateY(-3px)',
                        },
                      }}
                    >
                      Sign In
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Paper>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 12 }, px: { xs: 2, sm: 3 } }}>
        <Box textAlign="center" mb={{ xs: 6, sm: 8, md: 10 }}>
          <Chip
            label="✨ Features"
            sx={{
              bgcolor: '#667eea15',
              color: '#667eea',
              fontWeight: 'bold',
              mb: { xs: 2, md: 3 },
              fontSize: { xs: '0.85rem', md: '0.9rem' },
            }}
          />
          <Typography 
            variant="h2" 
            gutterBottom 
            fontWeight="900"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' },
              mb: 2,
              px: { xs: 2, sm: 0 },
            }}
          >
            Why Choose Videsa?
          </Typography>
          <Typography 
            variant="h6" 
            color="textSecondary" 
            sx={{ 
              maxWidth: 650, 
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
              px: { xs: 3, sm: 2 },
            }}
          >
            A comprehensive platform designed for modern education management with powerful features
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, sm: 3, md: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 4,
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                  border: '2px solid transparent',
                  borderRadius: 4,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${feature.color}15 0%, ${feature.color}05 100%)`,
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-12px)',
                    boxShadow: `0 20px 60px ${feature.color}30`,
                    borderColor: feature.color,
                    '&::before': {
                      opacity: 1,
                    },
                  },
                }}
              >
                <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                  <Box 
                    sx={{ 
                      color: feature.color,
                      mb: 3,
                      display: 'inline-flex',
                      p: 2.5,
                      borderRadius: '20px',
                      background: `linear-gradient(135deg, ${feature.color}20 0%, ${feature.color}10 100%)`,
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.1) rotate(5deg)',
                      },
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    fontWeight="bold"
                    sx={{ mb: 2, fontSize: '1.3rem' }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="textSecondary" 
                    sx={{ lineHeight: 1.8, fontSize: '0.95rem' }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Stats Section */}
      <Box sx={{ bgcolor: '#f8f9ff', py: { xs: 6, sm: 8 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 3, sm: 4 }}>
            {[
              { icon: <Groups sx={{ fontSize: 50 }} />, value: '10,000+', label: 'Active Students' },
              { icon: <EmojiEvents sx={{ fontSize: 50 }} />, value: '5,000+', label: 'Exams Conducted' },
              { icon: <AutoGraph sx={{ fontSize: 50 }} />, value: '98%', label: 'Success Rate' },
              { icon: <TrendingUp sx={{ fontSize: 50 }} />, value: '24/7', label: 'Support' },
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 4,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Box sx={{ color: '#667eea', mb: { xs: 1.5, md: 2 } }}>{stat.icon}</Box>
                  <Typography 
                    variant="h3" 
                    fontWeight="900" 
                    color="#667eea" 
                    gutterBottom
                    sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="textSecondary" 
                    fontWeight="600"
                    sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: { xs: 8, sm: 10, md: 12 },
          borderRadius: 0,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
          <Box textAlign="center" color="white">
            <Box
              sx={{
                display: 'inline-block',
                mb: 4,
                animation: 'float 3s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-20px)' },
                },
              }}
            >
              <EmojiEvents 
                sx={{ 
                  fontSize: 100, 
                  filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.3))',
                  color: '#ffd700',
                }} 
              />
            </Box>
            <Typography 
              variant="h2" 
              gutterBottom 
              fontWeight="900"
              sx={{
                fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' },
                textShadow: '2px 4px 12px rgba(0,0,0,0.2)',
                mb: { xs: 2, md: 3 },
                px: { xs: 1, sm: 0 },
              }}
            >
              Ready to Achieve Excellence?
            </Typography>
            <Typography 
              variant="h6" 
              paragraph 
              sx={{ 
                opacity: 0.95, 
                mb: { xs: 4, sm: 5, md: 6 },
                lineHeight: { xs: 1.6, md: 1.8 },
                maxWidth: 650,
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                textShadow: '1px 2px 6px rgba(0,0,0,0.15)',
                px: { xs: 2, sm: 0 },
              }}
            >
              Join thousands of students and educators transforming their academic journey with Videsa's powerful result management system
            </Typography>
            {!isAuthenticated && (
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 2, sm: 3 }, 
                justifyContent: 'center', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                px: { xs: 2, sm: 0 },
              }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: 'white',
                    color: '#667eea',
                    px: { xs: 4, sm: 6, md: 7 },
                    py: { xs: 2, sm: 2.5, md: 3 },
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                    fontWeight: 'bold',
                    borderRadius: '50px',
                    textTransform: 'none',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: '280px', sm: 'auto' },
                    '&:hover': { 
                      bgcolor: '#f8f9ff',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 20px 45px rgba(0,0,0,0.3)',
                    },
                  }}
                  onClick={() => navigate('/register')}
                >
                  Start Your Journey Today
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'white',
                    borderWidth: 2,
                    color: 'white',
                    px: { xs: 4, sm: 6, md: 7 },
                    py: { xs: 2, sm: 2.5, md: 3 },
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                    fontWeight: 'bold',
                    borderRadius: '50px',
                    textTransform: 'none',
                    backdropFilter: 'blur(10px)',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    transition: 'all 0.3s ease',
                    width: { xs: '100%', sm: 'auto' },
                    minWidth: { xs: '280px', sm: 'auto' },
                    '&:hover': { 
                      borderColor: 'white',
                      borderWidth: 2,
                      bgcolor: 'rgba(255,255,255,0.25)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => navigate('/login')}
                >
                  Learn More
                </Button>
              </Box>
            )}
          </Box>
        </Container>
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.08)',
              animation: `pulse ${4 + i}s ease-in-out infinite`,
              ...(i === 0 && { top: -100, right: -100, width: 350, height: 350 }),
              ...(i === 1 && { bottom: -80, left: -80, width: 300, height: 300 }),
              ...(i === 2 && { top: '50%', right: '10%', width: 200, height: 200 }),
            }}
          />
        ))}
      </Paper>

      {/* Footer */}
      <Box sx={{ 
        bgcolor: '#0a0a1a', 
        color: 'white', 
        py: { xs: 6, sm: 8, md: 10 },
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, #667eea, transparent)',
        },
      }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 4, sm: 5, md: 6 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <School 
                    sx={{ 
                      fontSize: 50, 
                      color: '#667eea',
                      filter: 'drop-shadow(0 0 20px rgba(102, 126, 234, 0.5))',
                    }} 
                  />
                  <Typography variant="h4" fontWeight="900" sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    Videsa
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.9, maxWidth: 350, mb: 4, fontSize: '0.95rem' }}>
                Transforming education management with innovative result tracking, comprehensive analytics, and powerful performance insights for the modern learning environment.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {['F', 'T', 'L', 'I'].map((social, index) => (
                  <Avatar
                    key={index}
                    sx={{
                      bgcolor: 'rgba(102, 126, 234, 0.15)',
                      width: 45,
                      height: 45,
                      cursor: 'pointer',
                      border: '1px solid rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: '#667eea',
                        transform: 'translateY(-5px) scale(1.1)',
                        boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)',
                      },
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {social}
                    </Typography>
                  </Avatar>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 3, color: '#667eea' }}>
                Platform
              </Typography>
              {['Features', 'Pricing', 'About Us', 'Careers'].map((item) => (
                <Typography
                  key={item}
                  variant="body2"
                  sx={{
                    opacity: 0.7,
                    mb: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 1,
                      color: '#667eea',
                      paddingLeft: '8px',
                    },
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 3, color: '#667eea' }}>
                Quick Links
              </Typography>
              {[
                { label: 'Home', path: '/' },
                { label: 'Login', path: '/login' },
                { label: 'Register', path: '/register' },
                { label: 'Dashboard', path: '/dashboard' },
              ].map((link) => (
                <Typography
                  key={link.label}
                  variant="body2"
                  sx={{
                    opacity: 0.7,
                    mb: 2,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 1,
                      color: '#667eea',
                      paddingLeft: '8px',
                    },
                  }}
                  onClick={() => navigate(link.path)}
                >
                  {link.label}
                </Typography>
              ))}
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 3, color: '#667eea' }}>
                Contact Us
              </Typography>
              <Typography component="div" variant="body2" sx={{ opacity: 0.7, mb: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: '#43e97b',
                    display: 'inline-block',
                    boxShadow: '0 0 10px rgba(67, 233, 123, 0.5)',
                  }}
                />
                support@videsa.lk
              </Typography>
              <Typography component="div" variant="body2" sx={{ opacity: 0.7, mb: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: '#4facfe',
                    display: 'inline-block',
                    boxShadow: '0 0 10px rgba(79, 172, 254, 0.5)',
                  }}
                />
                +94 76 613 1578
              </Typography>
              <Typography component="div" variant="body2" sx={{ opacity: 0.7, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: '#f093fb',
                    display: 'inline-block',
                    boxShadow: '0 0 10px rgba(240, 147, 251, 0.5)',
                  }}
                />
                Monday - Friday, 9AM - 5PM
              </Typography>
            </Grid>
          </Grid>
          <Box
            sx={{
              borderTop: '1px solid rgba(102, 126, 234, 0.2)',
              mt: { xs: 6, sm: 7, md: 8 },
              pt: { xs: 4, sm: 5, md: 6 },
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'space-between' },
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 3 },
            }}
          >
            <Typography variant="body2" sx={{ opacity: 0.6, fontSize: { xs: '0.85rem', sm: '0.9rem' }, textAlign: { xs: 'center', sm: 'left' } }}>
              © 2026 Videsa Exam Results System. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3, md: 4 }, flexWrap: 'wrap', justifyContent: 'center' }}>
              {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((item) => (
                <Typography
                  key={item}
                  variant="body2"
                  sx={{
                    opacity: 0.6,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 1,
                      color: '#667eea',
                    },
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
